import React, { useState, useEffect, useCallback } from 'react';
import { fetchTariffItems, fetchTariffSchedules, createTariffItem, updateTariffItem } from '../api';

const CATEGORY_CHOICES = [
  { value: 'INSTRUCTIONS', label: 'Instructions' },
  { value: 'PERUSAL', label: 'Perusal' },
  { value: 'ATTENDANCE', label: 'Attendance' },
  { value: 'DRAWING', label: 'Drawing' },
  { value: 'CONSULTATION', label: 'Consultation' },
  { value: 'COURT_APPEARANCE', label: 'Court Appearance' },
  { value: 'INTERLOCUTORY', label: 'Interlocutory' },
  { value: 'APPEAL', label: 'Appeal' },
  { value: 'DISBURSEMENT', label: 'Disbursement' },
  { value: 'OTHER', label: 'Other' },
];

const UNIT_TYPE_CHOICES = [
  { value: 'FIXED', label: 'Fixed' },
  { value: 'HOURLY', label: 'Hourly' },
  { value: 'PER_FOLIO', label: 'Per Folio' },
  { value: 'PERCENTAGE', label: 'Percentage' },
  { value: 'PER_ITEM', label: 'Per Item' },
];

const SENIORITY_CHOICES = [
  { value: 'PARTNER', label: 'Partner' },
  { value: 'PRINCIPAL', label: 'Principal' },
  { value: 'SENIOR', label: 'Senior Associate' },
  { value: 'JUNIOR', label: 'Junior Associate' },
  { value: 'PUPIL', label: 'Pupil' },
];

const EMPTY_FORM = {
  code: '',
  schedule: '',
  category: '',
  description: '',
  unit_type: '',
  attorney_client_rate: '',
  party_party_rate: '',
  seniority_level: '',
  notes: '',
  is_active: true,
};

function TariffCodesTab({ authAxios, canEdit, showFormMessage }) {
  const [items, setItems] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ count: 0, next: null, previous: null });
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 25;

  // Filters
  const [search, setSearch] = useState('');
  const [filterSchedule, setFilterSchedule] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSeniority, setFilterSeniority] = useState('');
  const [filterActive, setFilterActive] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // null = create, object = edit
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const loadSchedules = useCallback(async () => {
    try {
      const data = await fetchTariffSchedules(authAxios);
      setSchedules(Array.isArray(data) ? data : data?.results || []);
    } catch {
      // silently fail — schedules are a lookup
    }
  }, [authAxios]);

  const loadItems = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = { page_size: PAGE_SIZE, page: currentPage };
      if (search) params.search = search;
      if (filterSchedule) params.schedule = filterSchedule;
      if (filterCategory) params.category = filterCategory;
      if (filterSeniority) params.seniority_level = filterSeniority;
      if (filterActive !== '') params.is_active = filterActive;

      const data = await fetchTariffItems(authAxios, params);
      if (data?.results !== undefined) {
        setItems(data.results);
        setPagination({ count: data.count, next: data.next, previous: data.previous });
      } else {
        setItems(Array.isArray(data) ? data : []);
      }
    } catch {
      showFormMessage('error', 'Failed to load tariff codes.');
    } finally {
      setIsLoading(false);
    }
  }, [authAxios, currentPage, search, filterSchedule, filterCategory, filterSeniority, filterActive, showFormMessage]);

  useEffect(() => { loadSchedules(); }, [loadSchedules]);
  useEffect(() => { setCurrentPage(1); }, [search, filterSchedule, filterCategory, filterSeniority, filterActive]);
  useEffect(() => { loadItems(); }, [loadItems]);

  const openCreate = () => {
    setEditingItem(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({
      code: item.code || '',
      schedule: item.schedule || '',
      category: item.category || '',
      description: item.description || '',
      unit_type: item.unit_type || '',
      attorney_client_rate: item.attorney_client_rate ?? '',
      party_party_rate: item.party_party_rate ?? '',
      seniority_level: item.seniority_level || '',
      notes: item.notes || '',
      is_active: item.is_active !== false,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.code.trim()) errors.code = 'Code is required.';
    if (!formData.schedule) errors.schedule = 'Tariff schedule is required.';
    if (!formData.category) errors.category = 'Category is required.';
    if (!formData.description.trim()) errors.description = 'Description is required.';
    if (!formData.unit_type) errors.unit_type = 'Unit type is required.';
    if (formData.attorney_client_rate === '' || formData.attorney_client_rate === null)
      errors.attorney_client_rate = 'Attorney-Client rate is required.';
    if (!formData.seniority_level) errors.seniority_level = 'Seniority level is required.';
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        attorney_client_rate: parseFloat(formData.attorney_client_rate) || 0,
        party_party_rate: formData.party_party_rate !== '' ? parseFloat(formData.party_party_rate) : null,
        schedule: parseInt(formData.schedule, 10),
      };

      if (editingItem) {
        await updateTariffItem(authAxios, editingItem.id, payload);
        showFormMessage('success', 'Tariff code updated successfully.');
      } else {
        await createTariffItem(authAxios, payload);
        showFormMessage('success', 'Tariff code added. It is now available to all firms.');
      }
      closeModal();
      loadItems();
    } catch (err) {
      const detail = err?.response?.data;
      if (detail && typeof detail === 'object') {
        setFormErrors(detail);
      } else {
        showFormMessage('error', 'Failed to save tariff code.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await updateTariffItem(authAxios, item.id, { is_active: !item.is_active });
      loadItems();
    } catch {
      showFormMessage('error', 'Failed to update tariff status.');
    }
  };

  const totalPages = Math.ceil(pagination.count / PAGE_SIZE) || 1;
  const labelFor = (choices, value) => choices.find((c) => c.value === value)?.label || value || '—';
  const scheduleName = (id) => schedules.find((s) => s.id === id || s.id === parseInt(id, 10))?.name || id || '—';

  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-700">
        <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10A8 8 0 11 2 10a8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
        <span>
          Tariff codes are shared across all firms in the system. New tariffs added here become immediately available to every firm.
          Amendments to existing tariffs take effect on new bills; existing bill line items retain their original snapshotted rates.
        </span>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search code or description…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border rounded px-3 py-2 text-sm w-64"
        />
        <select value={filterSchedule} onChange={(e) => setFilterSchedule(e.target.value)} className="border rounded px-3 py-2 text-sm">
          <option value="">All Schedules</option>
          {schedules.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="border rounded px-3 py-2 text-sm">
          <option value="">All Categories</option>
          {CATEGORY_CHOICES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <select value={filterSeniority} onChange={(e) => setFilterSeniority(e.target.value)} className="border rounded px-3 py-2 text-sm">
          <option value="">All Seniorities</option>
          {SENIORITY_CHOICES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <select value={filterActive} onChange={(e) => setFilterActive(e.target.value)} className="border rounded px-3 py-2 text-sm">
          <option value="">Active &amp; Inactive</option>
          <option value="true">Active only</option>
          <option value="false">Inactive only</option>
        </select>
        <div className="ml-auto">
          {canEdit && (
            <button
              onClick={openCreate}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 font-medium"
            >
              + Add Tariff Code
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="text-center text-gray-400 py-12">Loading tariff codes…</div>
      ) : items.length === 0 ? (
        <div className="text-center text-gray-400 py-12">No tariff codes found.</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 text-left">Code</th>
                <th className="px-4 py-3 text-left">Schedule</th>
                <th className="px-4 py-3 text-left">Category</th>
                <th className="px-4 py-3 text-left">Description</th>
                <th className="px-4 py-3 text-left">Unit</th>
                <th className="px-4 py-3 text-right">A&amp;C Rate</th>
                <th className="px-4 py-3 text-right">P&amp;P Rate</th>
                <th className="px-4 py-3 text-left">Seniority</th>
                <th className="px-4 py-3 text-center">Status</th>
                {canEdit && <th className="px-4 py-3 text-center">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => (
                <tr key={item.id} className={`hover:bg-gray-50 ${!item.is_active ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3 font-mono font-medium text-gray-800">{item.code}</td>
                  <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate" title={scheduleName(item.schedule)}>
                    {scheduleName(item.schedule)}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{labelFor(CATEGORY_CHOICES, item.category)}</td>
                  <td className="px-4 py-3 text-gray-700 max-w-xs truncate" title={item.description}>{item.description}</td>
                  <td className="px-4 py-3 text-gray-600">{labelFor(UNIT_TYPE_CHOICES, item.unit_type)}</td>
                  <td className="px-4 py-3 text-right font-medium">
                    {item.attorney_client_rate != null ? `P ${parseFloat(item.attorney_client_rate).toFixed(2)}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-600">
                    {item.party_party_rate != null ? `P ${parseFloat(item.party_party_rate).toFixed(2)}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{labelFor(SENIORITY_CHOICES, item.seniority_level)}</td>
                  <td className="px-4 py-3 text-center">
                    {canEdit ? (
                      <button
                        onClick={() => handleToggleActive(item)}
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          item.is_active
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        {item.is_active ? 'Active' : 'Inactive'}
                      </button>
                    ) : (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {item.is_active ? 'Active' : 'Inactive'}
                      </span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => openEdit(item)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Edit tariff"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l-4 1 1-4 9.293-9.293a1 1 0 011.414 0l2.586 2.586a1 1 0 010 1.414L9 13z" />
                        </svg>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.count > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, pagination.count)} of {pagination.count}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={!pagination.previous}
              className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-3 py-1">{currentPage} / {totalPages}</span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={!pagination.next}
              className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-start justify-center z-50 py-8 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl mx-4">
            <h2 className="text-lg font-semibold mb-5">
              {editingItem ? `Edit Tariff — ${editingItem.code}` : 'Add New Tariff Code'}
            </h2>
            <form onSubmit={handleSubmit} noValidate>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {/* Code */}
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Code <span className="text-red-500">*</span></span>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="e.g. HC-99"
                    className={`mt-1 block w-full border rounded px-3 py-2 text-sm ${formErrors.code ? 'border-red-400' : ''}`}
                  />
                  {formErrors.code && <p className="text-red-500 text-xs mt-1">{formErrors.code}</p>}
                </label>

                {/* Tariff Schedule */}
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Tariff Schedule <span className="text-red-500">*</span></span>
                  <select
                    value={formData.schedule}
                    onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
                    className={`mt-1 block w-full border rounded px-3 py-2 text-sm ${formErrors.schedule ? 'border-red-400' : ''}`}
                  >
                    <option value="">— Select schedule —</option>
                    {schedules.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  {formErrors.schedule && <p className="text-red-500 text-xs mt-1">{formErrors.schedule}</p>}
                </label>

                {/* Category */}
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Category <span className="text-red-500">*</span></span>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className={`mt-1 block w-full border rounded px-3 py-2 text-sm ${formErrors.category ? 'border-red-400' : ''}`}
                  >
                    <option value="">— Select category —</option>
                    {CATEGORY_CHOICES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  {formErrors.category && <p className="text-red-500 text-xs mt-1">{formErrors.category}</p>}
                </label>

                {/* Unit Type */}
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Unit Type <span className="text-red-500">*</span></span>
                  <select
                    value={formData.unit_type}
                    onChange={(e) => setFormData({ ...formData, unit_type: e.target.value })}
                    className={`mt-1 block w-full border rounded px-3 py-2 text-sm ${formErrors.unit_type ? 'border-red-400' : ''}`}
                  >
                    <option value="">— Select unit type —</option>
                    {UNIT_TYPE_CHOICES.map((u) => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                  {formErrors.unit_type && <p className="text-red-500 text-xs mt-1">{formErrors.unit_type}</p>}
                </label>

                {/* Seniority */}
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Seniority Level <span className="text-red-500">*</span></span>
                  <select
                    value={formData.seniority_level}
                    onChange={(e) => setFormData({ ...formData, seniority_level: e.target.value })}
                    className={`mt-1 block w-full border rounded px-3 py-2 text-sm ${formErrors.seniority_level ? 'border-red-400' : ''}`}
                  >
                    <option value="">— Select seniority —</option>
                    {SENIORITY_CHOICES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                  {formErrors.seniority_level && <p className="text-red-500 text-xs mt-1">{formErrors.seniority_level}</p>}
                </label>

                {/* Is Active */}
                <div className="flex items-center gap-3 pt-6">
                  <input
                    id="is_active_check"
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600"
                  />
                  <label htmlFor="is_active_check" className="text-sm font-medium text-gray-700">Active</label>
                </div>

                {/* Description — full width */}
                <label className="block md:col-span-2">
                  <span className="text-sm font-medium text-gray-700">Description <span className="text-red-500">*</span></span>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className={`mt-1 block w-full border rounded px-3 py-2 text-sm ${formErrors.description ? 'border-red-400' : ''}`}
                    placeholder="Full statutory description of the tariff item"
                  />
                  {formErrors.description && <p className="text-red-500 text-xs mt-1">{formErrors.description}</p>}
                </label>

                {/* A&C Rate */}
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Attorney-Client Rate (P) <span className="text-red-500">*</span></span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.attorney_client_rate}
                    onChange={(e) => setFormData({ ...formData, attorney_client_rate: e.target.value })}
                    className={`mt-1 block w-full border rounded px-3 py-2 text-sm ${formErrors.attorney_client_rate ? 'border-red-400' : ''}`}
                    placeholder="0.00"
                  />
                  {formErrors.attorney_client_rate && <p className="text-red-500 text-xs mt-1">{formErrors.attorney_client_rate}</p>}
                </label>

                {/* P&P Rate */}
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Party-Party Rate (P)</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.party_party_rate}
                    onChange={(e) => setFormData({ ...formData, party_party_rate: e.target.value })}
                    className="mt-1 block w-full border rounded px-3 py-2 text-sm"
                    placeholder="Leave blank if not applicable"
                  />
                </label>

                {/* Notes — full width */}
                <label className="block md:col-span-2">
                  <span className="text-sm font-medium text-gray-700">Notes / Statutory Reference</span>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={2}
                    className="mt-1 block w-full border rounded px-3 py-2 text-sm"
                    placeholder="e.g. Rule 74(2)(a) — High Court Rules"
                  />
                </label>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium disabled:opacity-60"
                >
                  {isSaving ? 'Saving…' : editingItem ? 'Save Changes' : 'Add Tariff Code'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TariffCodesTab;
